import React, { useState, useEffect } from 'react';
import { HelpCircle, Save } from 'lucide-react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Slider } from 'antd';
import axiosInstance from '../../utils/axiosInterceptor';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
interface SettingsSectionProps {
  selectedAssessment?: any;
}

const jobRoles = [
  { value: 'frontend-developer', label: 'Frontend Developer' },
  { value: 'backend-developer', label: 'Backend Developer' },
  { value: 'full-stack-developer', label: 'Full Stack Developer' },
  { value: 'devops-engineer', label: 'DevOps Engineer' },
  { value: 'data-scientist', label: 'Data Scientist' },
  { value: 'ui-ux-designer', label: 'UI/UX Designer' },
  { value: 'product-manager', label: 'Product Manager' },
  { value: 'qa-engineer', label: 'QA Engineer' },
  { value: 'mobile-developer', label: 'Mobile Developer' },
  { value: 'security-engineer', label: 'Security Engineer' },
  { value: 'cloud-architect', label: 'Cloud Architect' },
  { value: 'machine-learning-engineer', label: 'Machine Learning Engineer' },
  { value: 'software-engineer', label: 'Software Engineer' },
  { value: 'technical-lead', label: 'Technical Lead' },
  { value: 'engineering-manager', label: 'Engineering Manager' },
];

const SettingsSection: React.FC<SettingsSectionProps> = ({ selectedAssessment }) => {
  const [assessmentName, setAssessmentName] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<any>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<any>(null);
  const [interviewDuration, setInterviewDuration] = useState(15);
  const [workExpMin, setWorkExpMin] = useState(0);
  const [workExpMax, setWorkExpMax] = useState(15);
  const [invitationMailSubject, setInvitationMailSubject] = useState('Interview Invitation - {{assessmentName}}');
  const [invitationMailContent, setInvitationMailContent] = useState(`Dear {{candidateName}},

We are pleased to invite you to participate in our interview assessment for the {{role}} position.

Job Description:
{{jd}}

Assessment Details:
- Role: {{role}}
- Duration: {{duration}} minutes
- Language: {{language}}

Please click on the link below to begin your interview:
{{interviewLink}}

Best regards,
{{companyName}} Team`);

  const languageOptions = [
    { value: 'english', label: 'English' },
  ];

  const difficultyOptions = [
    { value: 'Beginner', label: 'Beginner' },
    { value: 'Intermediate', label: 'Intermediate' },
    { value: 'Advanced', label: 'Advanced' },
  ];

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{
    assessmentName?: string;
    jobDescription?: string;
    role?: string;
    difficulty?: string;
    interviewLanguage?: string;
    duration?: string;
    workExp?: string;
    invitationMailSubject?: string;
    invitationMailContent?: string;
  }>({});
  const navigate = useNavigate();
  useEffect(() => {
    if (selectedAssessment) {
      setAssessmentName(selectedAssessment.assessmentName || selectedAssessment.assessmentRole || selectedAssessment.title || selectedAssessment.name || '');
      setJobDescription(selectedAssessment.jobDescription || selectedAssessment.description || '');
      // Find the role value from jobRoles that matches the assessment role
      const assessmentRoleValue = selectedAssessment.role || selectedAssessment.assessmentRole || '';
      // Check if the role exists in predefined options
      const matchingRole = jobRoles.find(r => r.value === assessmentRoleValue || r.label === assessmentRoleValue);
      // If not found in predefined options, create a custom option object
      if (assessmentRoleValue && !matchingRole) {
        setSelectedRole({ value: assessmentRoleValue, label: assessmentRoleValue });
      } else {
        setSelectedRole(matchingRole || null);
      }
      // Set difficulty
      const difficultyValue = selectedAssessment.difficulty || 'Beginner';
      const matchingDifficulty = difficultyOptions.find(d => d.value === difficultyValue);
      setSelectedDifficulty(matchingDifficulty || difficultyOptions[0]);
      // Set language
      const languageValue = selectedAssessment.interviewLanguage || 'english';
      const matchingLanguage = languageOptions.find(l => l.value === languageValue);
      setSelectedLanguage(matchingLanguage || languageOptions[0]);
      // Set duration
      setInterviewDuration(selectedAssessment.duration || 15);
      // Set work experience
      setWorkExpMin(selectedAssessment.minExp ?? 0);
      setWorkExpMax(selectedAssessment.maxExp ?? 15);
      // Set invitation mail subject if it exists
      if (selectedAssessment.invitationMailSubject) {
        setInvitationMailSubject(selectedAssessment.invitationMailSubject);
      }
      // Set invitation mail template if it exists
      if (selectedAssessment.invitationMailTemplate) {
        setInvitationMailContent(selectedAssessment.invitationMailTemplate);
      }
    }
  }, [selectedAssessment]);

  const validateFields = () => {
    const newErrors: typeof errors = {};

    if (!assessmentName.trim()) {
      newErrors.assessmentName = 'Assessment name is required';
    }
    if (!jobDescription.trim()) {
      newErrors.jobDescription = 'Job description is required';
    }
    if (!selectedRole) {
      newErrors.role = 'Role is required';
    }
    if (!selectedDifficulty) {
      newErrors.difficulty = 'Difficulty is required';
    }
    if (!selectedLanguage) {
      newErrors.interviewLanguage = 'Interview language is required';
    }
    if (!interviewDuration || interviewDuration < 5 || interviewDuration > 40) {
      newErrors.duration = 'Interview duration must be between 5 and 40 minutes';
    }
    if (workExpMin > workExpMax) {
      newErrors.workExp = 'Minimum experience cannot be greater than maximum';
    }
    if (!invitationMailSubject.trim()) {
      newErrors.invitationMailSubject = 'Invitation mail subject is required';
    }
    if (!invitationMailContent.trim()) {
      newErrors.invitationMailContent = 'Invitation mail content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveChanges = async () => {
    if (!selectedAssessment?._id) {
      toast.error('No assessment selected');
      return;
    }

    // Validate all fields
    if (!validateFields()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        assessmentName: assessmentName.trim(),
        jobDescription: jobDescription.trim(),
        role: selectedRole?.value || selectedRole?.label || '',
        difficulty: selectedDifficulty?.value || 'Beginner',
        interviewLanguage: selectedLanguage?.value || 'english',
        duration: interviewDuration,
        minExp: workExpMin,
        maxExp: workExpMax,
        invitationMailSubject: invitationMailSubject.trim(),
        invitationMailTemplate: invitationMailContent.trim(),
      };

      axiosInstance.put(`/assessments/${selectedAssessment._id}`, updateData).then((response)=>{
        toast.success('Settings saved successfully');
        Promise.resolve(setTimeout(()=>{
          navigate('/dashboard');
        },1000));
      }).catch((error)=>{
        toast.error('Failed to save settings please try again after some time');
      });

      // Clear errors on successful save
      setErrors({});
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
          <HelpCircle className="h-4 w-4 text-blue-500" />
        </div>
        
        <div className="space-y-6">
          {/* Assessment Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assessment Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={assessmentName}
              onChange={(e) => {
                setAssessmentName(e.target.value);
                if (errors.assessmentName) {
                  setErrors({ ...errors, assessmentName: undefined });
                }
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.assessmentName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter assessment name"
            />
            {errors.assessmentName && (
              <p className="mt-1 text-sm text-red-600">{errors.assessmentName}</p>
            )}
          </div>

          {/* Job Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={jobDescription}
              onChange={(e) => {
                setJobDescription(e.target.value);
                if (errors.jobDescription) {
                  setErrors({ ...errors, jobDescription: undefined });
                }
              }}
              rows={6}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                errors.jobDescription ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter job description"
            />
            {errors.jobDescription && (
              <p className="mt-1 text-sm text-red-600">{errors.jobDescription}</p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role <span className="text-red-500">*</span>
            </label>
            <CreatableSelect
              value={selectedRole}
              onChange={(option: any) => {
                setSelectedRole(option);
                if (errors.role) {
                  setErrors({ ...errors, role: undefined });
                }
              }}
              onCreateOption={(inputValue: string) => {
                const trimmedValue = inputValue.trim();
                if (trimmedValue.length > 0) {
                  const newOption = { value: trimmedValue, label: trimmedValue };
                  setSelectedRole(newOption);
                  if (errors.role) {
                    setErrors({ ...errors, role: undefined });
                  }
                }
              }}
              options={jobRoles}
              isSearchable={true}
              isClearable={true}
              allowCreateWhileLoading={false}
              createOptionPosition="first"
              placeholder="Type to search or create a new role..."
              className="react-select-container"
              classNamePrefix="react-select"
              formatCreateLabel={(inputValue: string) => `Create "${inputValue}"`}
              noOptionsMessage={({ inputValue }: { inputValue: string }) => 
                inputValue ? `Press Enter or click "Create "${inputValue}"" to add it` : 'No options available'
              }
              isValidNewOption={(inputValue: string) => {
                const trimmed = inputValue.trim();
                return trimmed.length > 0 && trimmed.length <= 50;
              }}
              getNewOptionData={(inputValue: string) => ({
                label: inputValue,
                value: inputValue,
                __isNew__: true,
              })}
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: errors.role ? '#ef4444' : state.isFocused ? '#3b82f6' : '#d1d5db',
                  boxShadow: state.isFocused && !errors.role ? '0 0 0 1px #3b82f6' : 'none',
                  '&:hover': {
                    borderColor: errors.role ? '#ef4444' : '#3b82f6',
                  },
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
                menuList: (base) => ({
                  ...base,
                  padding: 0,
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected
                    ? '#3b82f6'
                    : state.isFocused
                    ? '#eff6ff'
                    : 'white',
                  color: state.isSelected ? 'white' : '#1f2937',
                  cursor: 'pointer',
                  '&:active': {
                    backgroundColor: '#3b82f6',
                    color: 'white',
                  },
                }),
                input: (base) => ({
                  ...base,
                  color: '#1f2937',
                }),
                placeholder: (base) => ({
                  ...base,
                  color: '#9ca3af',
                }),
                singleValue: (base) => ({
                  ...base,
                  color: '#1f2937',
                }),
              }}
            />
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role}</p>
            )}
          </div>

          {/* Difficulty */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Difficulty <span className="text-red-500">*</span>
            </label>
            <Select
              value={selectedDifficulty}
              onChange={(option) => {
                setSelectedDifficulty(option);
                if (errors.difficulty) {
                  setErrors({ ...errors, difficulty: undefined });
                }
              }}
              options={difficultyOptions}
              isSearchable={false}
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: errors.difficulty ? '#ef4444' : state.isFocused ? '#3b82f6' : '#d1d5db',
                  boxShadow: state.isFocused && !errors.difficulty ? '0 0 0 1px #3b82f6' : 'none',
                  '&:hover': {
                    borderColor: errors.difficulty ? '#ef4444' : '#3b82f6',
                  },
                }),
              }}
            />
            {errors.difficulty && (
              <p className="mt-1 text-sm text-red-600">{errors.difficulty}</p>
            )}
          </div>

          {/* Interview Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Interview Language <span className="text-red-500">*</span>
            </label>
            <Select
              value={selectedLanguage}
              onChange={(option) => {
                setSelectedLanguage(option);
                if (errors.interviewLanguage) {
                  setErrors({ ...errors, interviewLanguage: undefined });
                }
              }}
              options={languageOptions}
              isSearchable={false}
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: errors.interviewLanguage ? '#ef4444' : state.isFocused ? '#3b82f6' : '#d1d5db',
                  boxShadow: state.isFocused && !errors.interviewLanguage ? '0 0 0 1px #3b82f6' : 'none',
                  '&:hover': {
                    borderColor: errors.interviewLanguage ? '#ef4444' : '#3b82f6',
                  },
                }),
              }}
            />
            {errors.interviewLanguage && (
              <p className="mt-1 text-sm text-red-600">{errors.interviewLanguage}</p>
            )}
          </div>

          {/* Interview Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Interview Duration (Minutes) <span className="text-red-500">*</span>
              <span className="ml-2 text-blue-600 font-normal">({interviewDuration} min)</span>
            </label>
            <div className="px-2">
              <Slider
                min={5}
                max={40}
                step={5}
                value={interviewDuration}
                onChange={(value) => {
                  setInterviewDuration(value);
                  if (errors.duration) {
                    setErrors({ ...errors, duration: '' });
                  }
                }}
                marks={{
                  5: '5',
                  40: '40'
                }}
                tooltip={{
                  formatter: (value) => `${value} min`
                }}
              />
            </div>
            {errors.duration && (
              <p className="mt-1 text-sm text-red-600">{errors.duration}</p>
            )}
          </div>

          {/* Expected Work Experience */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expected Work Experience (Years) <span className="text-red-500">*</span>
              <span className="ml-2 text-blue-600 font-normal">({workExpMin} - {workExpMax} years)</span>
            </label>
            <div className="px-2">
              <Slider
                range
                min={0}
                max={15}
                value={[workExpMin, workExpMax]}
                onChange={(values) => {
                  if (Array.isArray(values) && values.length === 2) {
                    setWorkExpMin(values[0]);
                    setWorkExpMax(values[1]);
                    if (errors.workExp) {
                      setErrors({ ...errors, workExp: '' });
                    }
                  }
                }}
                marks={{
                  0: '0',
                  15: '15'
                }}
                tooltip={{
                  formatter: (value) => `${value} years`
                }}
              />
            </div>
            {errors.workExp && (
              <p className="mt-1 text-sm text-red-600">{errors.workExp}</p>
            )}
          </div>

          {/* Invitation Mail Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invitation Mail Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={invitationMailSubject}
              onChange={(e) => {
                setInvitationMailSubject(e.target.value);
                if (errors.invitationMailSubject) {
                  setErrors({ ...errors, invitationMailSubject: undefined });
                }
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.invitationMailSubject ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter email subject..."
            />
            {errors.invitationMailSubject && (
              <p className="mt-1 text-sm text-red-600">{errors.invitationMailSubject}</p>
            )}
          </div>

          {/* Invitation Mail Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invitation Mail Preview <span className="text-red-500">*</span>
            </label>
            <textarea
              value={invitationMailContent}
              onChange={(e) => {
                setInvitationMailContent(e.target.value);
                if (errors.invitationMailContent) {
                  setErrors({ ...errors, invitationMailContent: undefined });
                }
              }}
              rows={20}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm ${
                errors.invitationMailContent ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter invitation mail content..."
            />
            {errors.invitationMailContent && (
              <p className="mt-1 text-sm text-red-600">{errors.invitationMailContent}</p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              This is how your invitation email will look. You can edit the content above.
            </p>
          </div>
        </div>

        {/* Save Changes Button */}
        <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={handleSaveChanges}
            disabled={saving || !selectedAssessment?._id}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsSection;
